import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { AuthContextType, AuthState } from '../types/auth';
import { IUser } from '../types/user';
import { VERIFY_LOGGED_IN } from '../graphql/mutations/mutations';
import { Spin } from 'antd';
import TagManager from 'react-gtm-module';

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [auth, setAuth] = useState<AuthState>({
    user: null,
    loggedIn: false,
    firstAuthValidationDone: false,
    loaded: false,
  });

  const [checkIfLoggedIn, { loading }] = useMutation(VERIFY_LOGGED_IN, {
    onCompleted: data => {
      if (data?.CheckIfLoggedIn?.id) {
        setFirstAuthState(data.CheckIfLoggedIn, true);
      } else {
        setFirstAuthState(null, false);
      }
    },
    onError: () => {
      setFirstAuthState(null, false);
    },
  });

  useEffect(() => {
    checkIfLoggedIn();
  }, [checkIfLoggedIn]);

  useEffect(() => {
    const role = auth.user?.roles?.[0] || 'guest';
    TagManager.dataLayer({
      dataLayer: { user_role: role },
    });
  }, [auth.user]);

  const setAuthUser = (user: IUser) => {
    setAuth(prev => ({
      ...prev,
      user,
      loggedIn: true,
      loaded: true,
    }));
  };

  const setFirstAuthState = (user: IUser | null, loggedIn: boolean) => {
    setAuth(prev => ({
      ...prev,
      user,
      loggedIn,
      firstAuthValidationDone: true,
      loaded: true,
    }));
  };

  const removeAuthUser = () => {
    setAuth(prev => ({
      ...prev,
      user: null,
      loggedIn: false,
      loaded: true,
    }));
  };

  if ((loading && !auth.firstAuthValidationDone) || !auth.loaded) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100dvh',
        }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        ...auth,
        setAuthUser,
        setFirstAuthState,
        removeAuthUser,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
